import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form2290GrossWeight = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 2290 Taxable Gross Weight & Weight Categories Overview"
          description="Explore IRS Form 2290 taxable gross weight categories for commercial vehicles. Learn accurate calculation methods for HVUT at 55,000 pounds or more."
          keywords="Form 2290, Taxable gross weight, Weight categories, Tax filing for trucks, form 2290 weight category, form 2290 taxable gross weight"
          canonical="resources/excise-tax/form-2290-taxable-gross-weight-and-weight-categories/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 2290{' '}
          <span className="highlight-container">
            <span className="highlight">Taxable Gross Weight</span>
          </span>{' '}
          &amp;{' '}
          <span className="highlight-container">
            <span className="highlight">Weight Categories</span>
          </span>{' '}
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/form-2290-taxable-gross-weight-and-weight-categories/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        Form 2290 Taxable Gross Weight at a Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemScope
                        itemType="https://schema.org/ItemList"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="1"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              What It Means:
                            </strong>{' '}
                            Taxable gross weight includes the vehicle’s{' '}
                            <strong style={{ fontWeight: 600 }}>
                              weight, regularly used trailers, and maximum load
                              capacity.
                            </strong>
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="2"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Filing Threshold:
                            </strong>{' '}
                            Vehicles with a taxable gross weight of{' '}
                            <strong style={{ fontWeight: 600 }}>
                              55,000 pounds or more
                            </strong>{' '}
                            are generally subject to HVUT.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="3"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Weight Categories:
                            </strong>{' '}
                            Form 2290 uses{' '}
                            <strong style={{ fontWeight: 600 }}>
                              Categories A–V
                            </strong>{' '}
                            to determine the applicable tax based on taxable
                            gross weight.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="4"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Category W:
                            </strong>{' '}
                            Vehicles driven{' '}
                            <strong style={{ fontWeight: 600 }}>
                              5,000 miles or less
                            </strong>{' '}
                            (7,500 miles for agricultural vehicles) may qualify
                            as suspended vehicles and generally owe no HVUT.
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="5"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Tax Rates:
                            </strong>{' '}
                            HVUT increases with taxable gross weight, with rates
                            reaching{' '}
                            <strong style={{ fontWeight: 600 }}>
                              $550 for vehicles over 75,000 pounds.
                            </strong>
                          </span>
                        </li>

                        <li
                          itemProp="itemListElement"
                          itemScope
                          itemType="https://schema.org/ListItem"
                        >
                          <data
                            itemProp="position"
                            value="6"
                            style={{ display: 'none' }}
                          />

                          <span itemProp="name">
                            <strong style={{ fontWeight: 600 }}>
                              Prorated Tax:
                            </strong>{' '}
                            Vehicles first used during the tax year may have
                            their HVUT prorated based on the{' '}
                            <strong style={{ fontWeight: 600 }}>
                              first-used month.
                            </strong>
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>

                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      IRS Tax Form 2290, the Heavy Highway Vehicle Use Tax
                      Return, is an annual tax form filed by owners and
                      operators of heavy vehicles. One of the critical factors
                      in determining the tax liability on{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 2290
                      </Link>{' '}
                      is the taxable gross weight of the vehicle. Understanding
                      the weight categories and the corresponding tax rates is
                      crucial for accurate tax computation.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      In this article, we will learn about taxable gross weight
                      and weight categories and how you can accurately calculate
                      and pay your HVUT.
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is a taxable gross weight?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#taxable" sx={{ color: '#0202ff' }}>
                          Taxable gross weight categories from A to V
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#category" sx={{ color: '#0202ff' }}>
                          What is category W?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#table" sx={{ color: '#0202ff' }}>
                          Tax Computation Table
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pay" sx={{ color: '#0202ff' }}>
                          Pay your HVUT
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What is Taxable Gross Weight?
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  <b>Taxable gross weight</b> means the{' '}
                  <b>total weight a heavy vehicle can legally carry</b>.
                  It&apos;s calculated by adding three things:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li>
                    The weight of the <b>empty vehicle itself</b>, including all
                    its equipment ready for use.
                  </li>
                  <li>
                    The weight of any empty trailers you regularly use with the
                    vehicle.
                  </li>
                  <li>
                    The <b>maximum weight</b> the vehicle and trailers are
                    designed to carry (not the actual load weight).
                  </li>
                </ul>

                <Typography variant="body1" sx={{ mb: 5 }}>
                  Taxable gross weight is a fundamental element in calculating
                  the Heavy Vehicle Use Tax (HVUT) owed by heavy vehicle owners
                  on{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    2290 tax form
                  </Link>
                  . The tax amount is directly proportional to the
                  vehicle&apos;s taxable gross weight and the type of use,
                  whether regular or logging.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="taxable"
                >
                  Taxable Gross Weight Categories from A to V
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  It is significant to know the taxable gross weight of your
                  vehicle as it determines whether you need to pay Form 2290
                  HVUT.
                </Typography>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    //  backgroundColor: '#f9f9f9',
                    border: '1px solid #ccc',
                    mb: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0 }}>
                    If your vehicle has a{' '}
                    <b>taxable gross weight of 55,000 pounds</b>, then you need
                    to pay the <b>HVUT</b> by filing <b>IRS Form 2290</b>.
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 2290 encompasses taxable weight categories from A to V,
                  each representing a specific range of taxable gross weight.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here&apos;s the table summarizing the taxable gross weight for
                  each category:
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Taxable Gross Weight Category
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      '& th': { py: 1 },
                      '& td': { py: 0.5 },
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Category
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Taxable Gross Weight (lbs)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>A</TableCell>
                          <TableCell>55,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>B</TableCell>
                          <TableCell>55,001 - 56,000</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>C</TableCell>
                          <TableCell>56,001 - 57,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>D</TableCell>
                          <TableCell>57,001 - 58,000</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>E</TableCell>
                          <TableCell>58,001 - 59,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>F</TableCell>
                          <TableCell>59,001 - 60,000</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>G</TableCell>
                          <TableCell>60,001 - 61,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>H</TableCell>
                          <TableCell>61,001 - 62,000</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>I</TableCell>
                          <TableCell>62,001 - 63,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>J</TableCell>
                          <TableCell>63,001 - 64,000</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>K</TableCell>
                          <TableCell>64,001 - 65,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>L</TableCell>
                          <TableCell>65,001 - 66,000</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>M</TableCell>
                          <TableCell>66,001 - 67,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>N</TableCell>
                          <TableCell>67,001 - 68,000</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>O</TableCell>
                          <TableCell>68,001 - 69,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>P</TableCell>
                          <TableCell>69,001 - 70,000</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Q</TableCell>
                          <TableCell>70,001 - 71,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>R</TableCell>
                          <TableCell>71,001 - 72,000</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>S</TableCell>
                          <TableCell>72,001 - 73,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>T</TableCell>
                          <TableCell>73,001 - 74,000</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>U</TableCell>
                          <TableCell>74,001 - 75,000</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>V</TableCell>
                          <TableCell>Over 75,000</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="category"
                >
                  What is Category W?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Tax suspended vehicles designated as Category W are commonly
                  known as low-mileage vehicles. These are vehicles used on
                  public highways that do not exceed 5,000 miles (7,500 miles
                  for agricultural vehicles) during the tax period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  If you own a low mileage vehicle, you must file IRS Form 2290
                  and report the vehicle under &ldquo;
                  <b>
                    <Link
                      href="/resources/excise-tax/form-2290-taxable-suspended-vehicles/"
                      sx={{ color: '#0202ff' }}
                    >
                      Suspended Vehicles
                    </Link>
                  </b>
                  &rdquo;. You will not owe any taxes for these vehicles, but
                  you will still need to file 2290 tax form and report them to
                  the IRS.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="table"
                >
                  Tax Computation Table
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Here&apos;s a table summarizing tax rate based on the taxable
                  gross weight and weight categories for both non-logging and
                  logging vehicles:
                </Typography>
                <Box component="div" sx={{ mb: 1 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      '& th': { py: 1 },
                      '& td': { py: 0.5 },
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444' }}>Category</TableCell>

                          <TableCell sx={{ color: '#444' }}>
                            Taxable Gross Weight (in pounds)
                          </TableCell>
                          <TableCell sx={{ color: '#444' }}>
                            Tax Amount for Non-logging Vehicles*
                          </TableCell>
                          <TableCell sx={{ color: '#444' }}>
                            Tax Amount for Logging Vehicles*
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>A</TableCell>
                          <TableCell>55,000</TableCell>
                          <TableCell>$100.00</TableCell>
                          <TableCell>$75.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>B</TableCell>
                          <TableCell>55,001 - 56,000</TableCell>
                          <TableCell>$122.00</TableCell>
                          <TableCell>$91.50</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>C</TableCell>
                          <TableCell>56,001 - 57,000</TableCell>
                          <TableCell>$144.00</TableCell>
                          <TableCell>$108.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>D</TableCell>
                          <TableCell>57,001 - 58,000</TableCell>
                          <TableCell>$166.00</TableCell>
                          <TableCell>$124.50</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>E</TableCell>
                          <TableCell>58,001 - 59,000</TableCell>
                          <TableCell>$188.00</TableCell>
                          <TableCell>$141.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>F</TableCell>
                          <TableCell>59,001 - 60,000</TableCell>
                          <TableCell>$210.00</TableCell>
                          <TableCell>$157.50</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>G</TableCell>
                          <TableCell>60,001 - 61,000</TableCell>
                          <TableCell>$232.00</TableCell>
                          <TableCell>$174.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>H</TableCell>
                          <TableCell>61,001 - 62,000</TableCell>
                          <TableCell>$254.00</TableCell>
                          <TableCell>$190.50</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>I</TableCell>
                          <TableCell>62,001 - 63,000</TableCell>
                          <TableCell>$276.00</TableCell>
                          <TableCell>$207.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>J</TableCell>
                          <TableCell>63,001 - 64,000</TableCell>
                          <TableCell>$298.00</TableCell>
                          <TableCell>$223.50</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>K</TableCell>
                          <TableCell>64,001 - 65,000</TableCell>
                          <TableCell>$320.00</TableCell>
                          <TableCell>$240.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>L</TableCell>
                          <TableCell>65,001 - 66,000</TableCell>
                          <TableCell>$342.00</TableCell>
                          <TableCell>$256.50</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>M</TableCell>
                          <TableCell>66,001 - 67,000</TableCell>
                          <TableCell>$364.00</TableCell>
                          <TableCell>$273.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>N</TableCell>
                          <TableCell>67,001 - 68,000</TableCell>
                          <TableCell>$386.00</TableCell>
                          <TableCell>$289.50</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>O</TableCell>
                          <TableCell>68,001 - 69,000</TableCell>
                          <TableCell>$408.00</TableCell>
                          <TableCell>$306.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>P</TableCell>
                          <TableCell>69,001 - 70,000</TableCell>
                          <TableCell>$430.00</TableCell>
                          <TableCell>$322.50</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Q</TableCell>
                          <TableCell>70,001 - 71,000</TableCell>
                          <TableCell>$452.00</TableCell>
                          <TableCell>$339.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>R</TableCell>
                          <TableCell>71,001 - 72,000</TableCell>
                          <TableCell>$474.00</TableCell>
                          <TableCell>$355.50</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>S</TableCell>
                          <TableCell>72,001 - 73,000</TableCell>
                          <TableCell>$496.00</TableCell>
                          <TableCell>$372.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>T</TableCell>
                          <TableCell>73,001 - 74,000</TableCell>
                          <TableCell>$518.00</TableCell>
                          <TableCell>$388.50</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>U</TableCell>
                          <TableCell>74,001 - 75,000</TableCell>
                          <TableCell>$540.00</TableCell>
                          <TableCell>$405.00</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>V</TableCell>
                          <TableCell>Over 75,000</TableCell>
                          <TableCell>$550.00</TableCell>
                          <TableCell>$412.50</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: 2, color: '#6f6c7f' }}
                >
                  *The tax amount is calculated with <b>July</b> as the{' '}
                  <b>first used month</b>.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If a heavy vehicle is first operated on public
                  highways during a tax year, the tax amount will be prorated
                  based on the months remaining in that tax year.
                </Alert>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  You can also use our{' '}
                  <Link
                    href="/tools/form-2290-tax-calculator/"
                    sx={{ color: '#0202ff' }}
                  >
                    HVUT Tax Calculator
                  </Link>{' '}
                  to find the exact HVUT amount you need to pay to the IRS.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="pay"
                >
                  Pay your HVUT
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Knowing your vehicle&apos;s taxable gross weight is
                  significant in determining the tax amount you must pay to the
                  IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In addition, if you incorrectly report your vehicle&apos;s
                  taxable gross weight, you could face penalties and fees for
                  incorrect filing. So, it&apos;s necessary to take the time to
                  accurately determine your vehicle&apos;s taxable gross weight
                  and report it correctly on your Form 2290.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can pay the HVUT due amount while filing Form 2290.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    textTransform: 'none',
                    boxShadow: 'none',
                    borderRadius: 2,
                  }}
                  size="large"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290weight`}
                >
                  E-File Form 2290 Now
                </Button>
                {/* <ol>
                  <li>
                    {' '}
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Enter your business information and vehicle details, such
                      as FUM and weight category, and TaxZerone will
                      automatically calculate the HVUT tax amount you need to
                      pay the IRS.
                    </Typography>
                  </li>
                  <li>
                    {' '}
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Review the information provided and make sure it is
                      accurate.
                    </Typography>
                  </li>
                  <li>
                    {' '}
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Transmit Form 2290 to the IRS and get your Schedule 1 copy
                      in minutes.
                    </Typography>
                  </li>
                </ol> */}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
    </AppLayout>
  );
};

export default Form2290GrossWeight;
